import { useHomeCalendar } from '../features/home/hooks/useHomeCalendar'
import type { HomeCalendarData } from '../services/home/types'

/**
 * The current user's home games, from the `/home/calendar` feed.
 *
 * A thin adapter over `useHomeCalendar` rather than its own fetch. It used to
 * be `useState` + a raw `req`, which meant the sidebar hit `/home/calendar`
 * TWICE on every mount — once here and once through the React Query hook the
 * events widget uses. Sharing the query means one request, one cache entry,
 * and one refetch when either consumer invalidates it.
 *
 * The return shape is unchanged, so existing callers need no edit. New code
 * should use `useHomeCalendar` directly — it exposes the events as well.
 */
interface UseHomeGamesResult {
  homeGames: HomeCalendarData | null
  loading: boolean
  error: Error | null
}

const useHomeGames = (): UseHomeGamesResult => {
  'use memo'

  const { data, isLoading, error } = useHomeCalendar()

  return {
    // `null` rather than `undefined` while loading, matching what callers
    // already branch on.
    homeGames: data ?? null,
    loading: isLoading,
    error,
  }
}

export default useHomeGames
